import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  Container,
  Stack,
} from '@mui/material';
import {
  LockOutlined,
  CheckCircle,
  RocketLaunch,
  Star,
  CreditCard,
} from '@mui/icons-material';

/**
 * Product configuration for the paywall display.
 */
interface PaywallProduct {
  slug: string;
  displayName: string;
  priceUSD: number;
  description: string;
  features: readonly string[];
}

interface SubscriptionStatus {
  has_subscription: boolean;
  status: string;
  product?: string;
  display_name?: string;
  price_usd?: number;
  trial_days?: number;
  trial_days_remaining?: number;
  reason?: string;
  customer_id?: string;
}

interface SubscriptionPaywallProps {
  product: PaywallProduct;
  apiBaseUrl?: string;
  children: React.ReactNode;
  getAuthToken?: () => Promise<string | null>;
  userId?: string;
  onSubscriptionActive?: () => void;
}

const SubscriptionPaywall: React.FC<SubscriptionPaywallProps> = ({
  product,
  apiBaseUrl = '',
  children,
  getAuthToken,
  userId,
  onSubscriptionActive,
}) => {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (getAuthToken) {
        const token = await getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      if (userId) {
        headers['X-User-ID'] = userId;
      }

      const response = await fetch(
        `${apiBaseUrl}/api/v1/billing/subscription/status?product=${product.slug}`,
        { headers, credentials: 'include' }
      );

      if (response.ok) {
        const data: SubscriptionStatus = await response.json();
        setStatus(data);

        if (data.has_subscription && onSubscriptionActive) {
          onSubscriptionActive();
        }
      } else {
        // If the endpoint doesn't exist yet, fail open
        setStatus({
          has_subscription: true,
          status: 'active',
          reason: 'endpoint_unavailable',
        });
      }
    } catch (err) {
      console.error('Subscription check failed:', err);
      setStatus({
        has_subscription: true,
        status: 'active',
        reason: 'check_failed',
      });
    } finally {
      setLoading(false);
    }
  }, [product.slug, apiBaseUrl, getAuthToken, userId, onSubscriptionActive]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_id')) {
      checkSubscription();
      const url = new URL(window.location.href);
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
    }
  }, [checkSubscription]);

  const handleCheckout = async () => {
    try {
      setCheckoutLoading(true);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (getAuthToken) {
        const token = await getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      if (userId) {
        headers['X-User-ID'] = userId;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/billing/checkout`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          product: product.slug,
          success_url: window.location.origin + '/dashboard?session_id={CHECKOUT_SESSION_ID}',
          cancel_url: window.location.href,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const err = await response.json().catch(() => ({ detail: 'Checkout failed' }));
        setError(err.detail || 'Failed to start checkout. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect to billing service. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setCheckoutLoading(true);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (getAuthToken) {
        const token = await getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      if (userId) {
        headers['X-User-ID'] = userId;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/billing/portal`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          return_url: window.location.href,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      } else {
        setError('Failed to open billing portal. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect to billing service.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">Checking subscription...</Typography>
      </Box>
    );
  }

  if (status?.has_subscription) {
    return <>{children}</>;
  }

  const isPastDue = status?.status === 'past_due';

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Container maxWidth="sm">
        <Card elevation={24} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(99, 102, 241, 0.3)', background: 'rgba(30, 41, 59, 0.95)', backdropFilter: 'blur(20px)' }}>
          <Box sx={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', py: 4, px: 3, textAlign: 'center' }}>
            <Box sx={{ mb: 2 }}>
              {isPastDue ? <CreditCard sx={{ fontSize: 48, color: '#fbbf24' }} /> : <RocketLaunch sx={{ fontSize: 48, color: 'white' }} />}
            </Box>
            <Typography variant="h4" fontWeight="bold" color="white" gutterBottom>
              {isPastDue ? 'Payment Required' : 'Start Your Free Trial'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              {isPastDue ? 'Your payment is overdue. Please update your payment method.' : `Try ${product.displayName} free for 7 days`}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {!isPastDue && (
              <>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Chip icon={<Star />} label="7-DAY FREE TRIAL" color="success" sx={{ mb: 2, fontWeight: 'bold', fontSize: '0.9rem' }} />
                  <Typography variant="h3" fontWeight="bold" color="white">
                    ${product.priceUSD}
                    <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 0.5 }}>/month</Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No charge for 7 days. Cancel anytime.</Typography>
                </Box>

                <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {product.features.map((feature, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircle sx={{ fontSize: 20, color: '#22c55e' }} />
                      <Typography variant="body2" color="text.secondary">{feature}</Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}

            <Button
              variant="contained" fullWidth size="large"
              onClick={isPastDue ? handleManageBilling : handleCheckout}
              disabled={checkoutLoading}
              startIcon={checkoutLoading ? <CircularProgress size={20} color="inherit" /> : isPastDue ? <CreditCard /> : <LockOutlined />}
              sx={{
                py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: 2,
                background: isPastDue ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                '&:hover': { background: isPastDue ? 'linear-gradient(135deg, #d97706, #b45309)' : 'linear-gradient(135deg, #4338ca, #6d28d9)' },
              }}
            >
              {checkoutLoading ? 'Redirecting...' : isPastDue ? 'Update Payment Method' : 'Start Free Trial'}
            </Button>

            {!isPastDue && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                <LockOutlined sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                Secure checkout powered by Stripe. Cancel anytime during your trial.
              </Typography>
            )}

            {isPastDue && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                You'll be redirected to Stripe to update your payment details.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SubscriptionPaywall;

/**
 * Pre-configured product definitions for all 5 EF apps — US pricing.
 */
export const EF_PRODUCTS = {
  'ef-strategy': {
    slug: 'ef-strategy',
    displayName: 'EF-Strategy',
    priceUSD: 149,
    description: 'AI-powered business strategy and executive intelligence',
    features: [
      'AI Strategy Advisor with multi-agent workflows',
      '300 strategic AI queries per month',
      'Business simulations & scenario planning',
      'Executive briefings & board reports',
      'Market research & competitor analysis',
      'Document intelligence & knowledge base',
      'Priority support',
    ],
  },
  'ef-crm': {
    slug: 'ef-crm',
    displayName: 'EF-CRM',
    priceUSD: 49,
    description: 'AI-powered customer relationship management',
    features: [
      'AI Sales Assistant & deal insights',
      '500 AI actions per month',
      'Pipeline forecasting & analytics',
      'Automated follow-up suggestions',
      'Lead scoring & qualification',
      'Email intelligence',
      'Standard support',
    ],
  },
  'ef-capflow': {
    slug: 'ef-capflow',
    displayName: 'EF-CapFlow',
    priceUSD: 49,
    description: 'AI-powered financial management & accounting',
    features: [
      'AI CFO Agent & financial insights',
      '500 AI queries per month',
      'Cash flow forecasting',
      'IRS integration & tax compliance',
      'Expense tracking & reporting',
      'Invoice management',
      'Standard support',
    ],
  },
  'ef-link': {
    slug: 'ef-link',
    displayName: 'EF-Link',
    priceUSD: 49,
    description: 'AI-powered supply chain intelligence',
    features: [
      'AI Supply Chain Advisor',
      '500 AI actions per month',
      'Supplier risk assessment',
      'Demand forecasting',
      'Inventory optimization',
      'Disruption alerts',
      'Standard support',
    ],
  },
  'ef-projex': {
    slug: 'ef-projex',
    displayName: 'EF-Projex',
    priceUSD: 49,
    description: 'AI-powered project management',
    features: [
      'AI Project Manager Agent',
      '500 AI actions per month',
      'Risk prediction & mitigation',
      'Resource optimization',
      'Status report generation',
      'Milestone tracking',
      'Standard support',
    ],
  },
} as const;

export type ProductSlug = keyof typeof EF_PRODUCTS;
